import { Box, Stack, Typography } from '@mui/material';
import type { ScoreHistoryPoint } from 'shared';
import { formatDateTime, toDate } from '../format';
import { MONO, scoreHex, tokens } from '../theme';

/**
 * Biểu đồ lịch sử điểm — spec SC-08 §3.2.
 *
 * **SVG viết tay, không thư viện biểu đồ.** Recharts/Chart.js kéo theo 100–300 KB
 * vào gói tải về cho một đường gấp khúc và năm đường kẻ ngang; và bản báo cáo
 * xuất ra file (spec §3.3) phải TỰ CHỨA, nên phần vẽ ở đó cũng là SVG viết tay —
 * dùng thư viện ở màn hình là nuôi hai cách vẽ cho cùng một biểu đồ.
 *
 * **Trục tung cố định 0–100**, không co theo dữ liệu: co theo dữ liệu thì một
 * trang dao động 82–84 điểm trông như một cơn địa chấn. Đây là biểu đồ để trả lời
 * "trang này đang tốt lên hay xấu đi", và một trục bị phóng đại trả lời sai.
 */

const W = 640;
const H = 190;
const PAD = { top: 10, right: 12, bottom: 22, left: 30 };

/**
 * `toDate` ở mọi chỗ chạm tới ngày: các trường này khai kiểu `Date` nhưng lúc chạy
 * là CHUỖI ISO — xem ghi chú ở `format.ts`. Gọi thẳng `.getTime()` lên chúng là
 * một màn hình trắng.
 */
function ngayNgan(d: Date | string): string {
  return toDate(d).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
}

function mocMs(d: Date | string): number {
  return toDate(d).getTime();
}

export function ScoreHistoryChart({ points }: { points: readonly ScoreHistoryPoint[] }) {
  const dau = points.at(0);
  const cuoi = points.at(-1);

  if (points.length < 2 || dau === undefined || cuoi === undefined) {
    return (
      <Typography sx={{ fontSize: 13, color: tokens.muted, py: 2 }}>
        {points.length === 0
          ? 'Chưa có lần quét nào để vẽ biểu đồ.'
          : 'Mới có một lần quét — biểu đồ cần ít nhất hai điểm mới nói lên được gì.'}
      </Typography>
    );
  }

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const t0 = mocMs(dau.scannedAt);
  const span = mocMs(cuoi.scannedAt) - t0;

  // Mọi lần quét trùng một mốc thời gian thì `span` bằng 0 — chia cho nó ra NaN và
  // cả biểu đồ biến mất không một lời báo. Rơi về khoảng cách đều.
  const x = (i: number, t: number): number =>
    PAD.left + (span === 0 ? (i / (points.length - 1)) * innerW : ((t - t0) / span) * innerW);
  const y = (v: number): number => PAD.top + innerH - (v / 100) * innerH;

  const duong = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i, mocMs(p.scannedAt)).toFixed(1)},${y(p.seoScore).toFixed(1)}`)
    .join(' ');

  // Vùng tô dưới đường: chỉ để mắt bám được đường đi, nên rất nhạt.
  const vung = `${duong} L${x(points.length - 1, mocMs(cuoi.scannedAt)).toFixed(1)},${(PAD.top + innerH).toFixed(1)} L${x(0, t0).toFixed(1)},${(PAD.top + innerH).toFixed(1)} Z`;

  const chenh = cuoi.seoScore - dau.seoScore;

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: tokens.muted }}>
          {points.length} lần quét · {ngayNgan(dau.scannedAt)} → {ngayNgan(cuoi.scannedAt)}
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: chenh > 0 ? tokens.okText : chenh < 0 ? tokens.roseText : tokens.faint,
          }}
        >
          {chenh > 0 ? `▲ ${chenh}` : chenh < 0 ? `▼ ${Math.abs(chenh)}` : 'không đổi'} điểm trong
          giai đoạn này
        </Typography>
      </Stack>

      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Biểu đồ lịch sử điểm SEO"
        sx={{ width: '100%', height: 'auto', display: 'block' }}
      >
        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line
              x1={PAD.left}
              y1={y(v)}
              x2={W - PAD.right}
              y2={y(v)}
              stroke={tokens.line2}
              strokeWidth={1}
            />
            <text
              x={PAD.left - 5}
              y={y(v) + 3.5}
              textAnchor="end"
              fontSize={10}
              fontFamily={MONO}
              fill={tokens.faint}
            >
              {v}
            </text>
          </g>
        ))}

        <path d={vung} fill={tokens.teal} opacity={0.07} />
        <path d={duong} fill="none" stroke={tokens.teal} strokeWidth={2} />

        {points.map((p, i) => (
          <circle
            key={p.scanId}
            cx={x(i, mocMs(p.scannedAt))}
            cy={y(p.seoScore)}
            r={3}
            fill={scoreHex(p.seoScore)}
          >
            {/* `<title>` là tooltip gốc của SVG — không cần thư viện, và đọc được
                bằng trình đọc màn hình. */}
            <title>{`${formatDateTime(p.scannedAt)} — ${p.seoScore} điểm`}</title>
          </circle>
        ))}

        <text x={PAD.left} y={H - 6} fontSize={10} fontFamily={MONO} fill={tokens.faint}>
          {ngayNgan(dau.scannedAt)}
        </text>
        <text
          x={W - PAD.right}
          y={H - 6}
          textAnchor="end"
          fontSize={10}
          fontFamily={MONO}
          fill={tokens.faint}
        >
          {ngayNgan(cuoi.scannedAt)}
        </text>
      </Box>
    </Box>
  );
}
